import { useState } from 'react';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  InlineError,
  Input,
  Select,
  Skeleton,
  Textarea,
} from '../../components/ui';
import { useTapPeople } from '../projects/hooks';
import { durationLabel, statusLabel, statusTone } from './leaveLens';
import {
  RANGE_LABELS,
  RANGE_ORDER,
  filterSummary,
  presetOf,
  rowActions,
  withPreset,
} from './allRequestsLens';
import type { RangePreset } from './allRequestsLens';
import {
  EMPTY_ALL_FILTER,
  leaveErrorMessage,
  useAdminEditLeave,
  useAllRequests,
  useDeleteLeaveRequest,
} from './queries';
import type { AllRequestFilter, AllRequestRow, DurationType, LeaveType } from './queries';

/**
 * All Requests — the team's leave, for the two people allowed to see it.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * The Apps Script has it and the Hub did not. Drops 1 to 5 only ever showed a
 * person their own leave, which meant the one screen an approver actually
 * opens to answer "what has everybody asked for" was missing. Kyu found it on
 * 7 September.
 *
 * ── Why the reasons are printed, when nothing else prints them ─────────────
 *
 * Because of who can open this. Kyu ruled on 7 September that All Requests is
 * for approvers only, and an approver was always allowed to read a reason —
 * `leave_request_private` has said so since drop 1. So the 4 September privacy
 * ruling is not being bent here; the audience simply never widens.
 *
 * The rows still cannot come from a select. `reason` and `admin_note` sit
 * outside the table's column grant, and `leave_all_requests` is the only path
 * to them for a set of requests. It refuses a non-approver outright, so this
 * screen being hidden from members is a convenience rather than the control.
 *
 * ── Why Apply rather than filtering as you type ────────────────────────────
 *
 * The Apps Script has an Apply Filters button and the filtering happens in the
 * database. Both are kept. A keystroke-per-request screen that ships reasons
 * over the wire on every character is a worse thing than a button.
 */

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING', label: 'Waiting for approval' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Not approved' },
  { value: 'CANCEL_REQUESTED', label: 'Withdrawal requested' },
  { value: 'CANCELLED', label: 'Withdrawn' },
];

export function AllRequests({ today }: { today: string }) {
  // Two filters, deliberately. `draft` is what the boxes hold; `applied` is
  // what the database was asked for. Without the split, every keystroke in a
  // date box would be a query, and this query returns reasons.
  const [draft, setDraft] = useState<AllRequestFilter>(EMPTY_ALL_FILTER);
  const [applied, setApplied] = useState<AllRequestFilter>(EMPTY_ALL_FILTER);
  const [editing, setEditing] = useState<AllRequestRow | null>(null);
  const [removing, setRemoving] = useState<AllRequestRow | null>(null);

  const rows = useAllRequests(applied, true);
  const people = useTapPeople(true);

  const peopleOptions = [
    { value: '', label: 'Everyone' },
    ...(people.data ?? []).map((person) => ({
      value: person.id,
      label: person.display_name ?? person.email,
    })),
  ];
  const employeeName =
    applied.employeeId === ''
      ? ''
      : (peopleOptions.find((option) => option.value === applied.employeeId)?.label ?? '');

  return (
    <div className="leave-all">
      <div className="leave-filters">
        <Select
          label="Time range"
          options={RANGE_ORDER.map((key) => ({ value: key, label: RANGE_LABELS[key] }))}
          value={presetOf(draft, today)}
          onChange={(event) => {
            setDraft(withPreset(draft, event.target.value as RangePreset, today));
          }}
        />
        <Input
          label="Start date"
          type="date"
          value={draft.from}
          onChange={(event) => {
            setDraft({ ...draft, from: event.target.value });
          }}
        />
        <Input
          label="End date"
          type="date"
          value={draft.to}
          onChange={(event) => {
            setDraft({ ...draft, to: event.target.value });
          }}
        />
        <Select
          label="Employee"
          options={peopleOptions}
          value={draft.employeeId}
          onChange={(event) => {
            setDraft({ ...draft, employeeId: event.target.value });
          }}
        />
        <Select
          label="Status"
          options={STATUS_OPTIONS}
          value={draft.status}
          onChange={(event) => {
            setDraft({ ...draft, status: event.target.value });
          }}
        />
        <div className="leave-filter-actions">
          <Button
            type="button"
            onClick={() => {
              setApplied(draft);
            }}
          >
            Apply Filters
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setDraft(EMPTY_ALL_FILTER);
              setApplied(EMPTY_ALL_FILTER);
            }}
          >
            Reset
          </Button>
        </div>
      </div>

      {rows.isLoading ? (
        <Skeleton />
      ) : rows.isError ? (
        <InlineError>{leaveErrorMessage(String(rows.error))}</InlineError>
      ) : (rows.data ?? []).length === 0 ? (
        <EmptyState
          title="Nothing matches those filters"
          description="Widen the range, or reset to see everything."
        />
      ) : (
        <>
          <p className="leave-filter-summary">
            {filterSummary(applied, (rows.data ?? []).length, employeeName)}
          </p>
          <div className="leave-table-scroll">
            <table className="tracker-table leave-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Duration</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Approver note</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(rows.data ?? []).map((row) => {
                  const can = rowActions(row.status);
                  return (
                    <tr key={row.id}>
                      <td className="leave-cell-name">{row.employee_name}</td>
                      <td>{row.leave_date}</td>
                      <td>{row.leave_type}</td>
                      <td>{durationLabel(row)}</td>
                      {/* An em dash rather than an empty cell: a blank reads as
                          a rendering failure, and a person needs to know the
                          box was left empty rather than lost. */}
                      <td className="leave-cell-reason">{row.reason === '' ? '—' : row.reason}</td>
                      <td>
                        <Badge tone={statusTone(row.status)}>{statusLabel(row.status)}</Badge>
                      </td>
                      <td className="leave-cell-note">
                        {row.admin_note === '' ? '—' : row.admin_note}
                      </td>
                      <td className="leave-cell-actions">
                        {/* The flex box is the div, never the cell — see the
                            same note on CG Orders' actions cell. */}
                        <div className="leave-cell-actions-group">
                          {can.canEdit ? (
                            <Button
                              variant="ghost"
                              onClick={() => {
                                setEditing(row);
                              }}
                            >
                              Edit
                            </Button>
                          ) : null}
                          {can.canDelete ? (
                            <Button
                              variant="ghost"
                              onClick={() => {
                                setRemoving(row);
                              }}
                            >
                              Delete
                            </Button>
                          ) : null}
                          {/* Neither offered: say so, rather than an empty cell
                            that looks like the buttons failed to draw. */}
                          {!can.canEdit && !can.canDelete ? (
                            <span className="leave-cell-quiet">Finished</span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {editing !== null && (
        <EditDialog
          row={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
      {removing !== null && (
        <DeleteDialog
          row={removing}
          onClose={() => {
            setRemoving(null);
          }}
        />
      )}
    </div>
  );
}

function EditDialog({ row, onClose }: { row: AllRequestRow; onClose: () => void }) {
  const edit = useAdminEditLeave();
  const [leaveDate, setLeaveDate] = useState(row.leave_date);
  const [leaveType, setLeaveType] = useState<LeaveType>(row.leave_type as LeaveType);
  const [durationType, setDurationType] = useState<DurationType>(row.duration_type as DurationType);
  const [startTime, setStartTime] = useState((row.start_time ?? '09:00').slice(0, 5));
  const [endTime, setEndTime] = useState((row.end_time ?? '13:00').slice(0, 5));
  const [reason, setReason] = useState(row.reason);
  const [note, setNote] = useState(row.admin_note);
  const [error, setError] = useState('');

  const submit = () => {
    setError('');
    edit.mutate(
      {
        requestId: row.id,
        leaveDate,
        leaveType,
        durationType,
        ...(durationType === 'Half Day' ? { startTime, endTime } : {}),
        reason,
        note,
      },
      {
        onSuccess: onClose,
        onError: (err: Error) => {
          setError(leaveErrorMessage(err.message));
        },
      },
    );
  };

  return (
    <Dialog
      open
      title={`Edit ${row.employee_name}'s request`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={edit.isPending || leaveDate === ''}>
            {edit.isPending ? 'Saving...' : 'Save changes'}
          </Button>
        </>
      }
    >
      <Input
        label="Date"
        type="date"
        value={leaveDate}
        onChange={(event) => {
          setLeaveDate(event.target.value);
        }}
      />
      <Select
        label="Type"
        options={[
          { value: 'PTO', label: 'PTO' },
          { value: 'Unpaid Leave', label: 'Unpaid Leave' },
        ]}
        value={leaveType}
        onChange={(event) => {
          setLeaveType(event.target.value as LeaveType);
        }}
      />
      <Select
        label="Duration"
        options={[
          { value: 'Full Day', label: 'Full Day' },
          { value: 'Half Day', label: 'Half Day' },
        ]}
        value={durationType}
        onChange={(event) => {
          setDurationType(event.target.value as DurationType);
        }}
      />
      {durationType === 'Half Day' && (
        <div className="leave-hours">
          <Input
            label="From"
            type="time"
            value={startTime}
            onChange={(event) => {
              setStartTime(event.target.value);
            }}
          />
          <Input
            label="To"
            type="time"
            value={endTime}
            onChange={(event) => {
              setEndTime(event.target.value);
            }}
          />
        </div>
      )}
      <Textarea
        label="Reason"
        value={reason}
        onChange={(event) => {
          setReason(event.target.value);
        }}
      />
      <Textarea
        label="Approver note"
        hint="Replaces whatever note is there now. The person sees this."
        value={note}
        onChange={(event) => {
          setNote(event.target.value);
        }}
      />
      {error === '' ? null : <InlineError>{error}</InlineError>}
    </Dialog>
  );
}

/**
 * Removing a request.
 *
 * The note is required by the database, not just by this form, and the wording
 * says why: once the row is gone the note and the snapshot beside it are the
 * only record that it ever existed.
 */
function DeleteDialog({ row, onClose }: { row: AllRequestRow; onClose: () => void }) {
  const remove = useDeleteLeaveRequest();
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const submit = () => {
    setError('');
    remove.mutate(
      { requestId: row.id, note },
      {
        onSuccess: onClose,
        onError: (err: Error) => {
          setError(leaveErrorMessage(err.message));
        },
      },
    );
  };

  return (
    <Dialog
      open
      title="Remove this request"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Keep it
          </Button>
          <Button onClick={submit} disabled={remove.isPending || note.trim() === ''}>
            {remove.isPending ? 'Removing...' : 'Remove request'}
          </Button>
        </>
      }
    >
      <p className="leave-remove-lede">
        {row.employee_name}, {row.leave_date}, {row.leave_type}, {durationLabel(row)}.
      </p>
      <p className="leave-remove-lede">
        The request goes. A record of this removal stays, carrying your note and the details above,
        and it cannot be undone from here.
      </p>
      <Textarea
        label="Why is it being removed?"
        hint="Required. This is the only explanation anybody will find afterwards."
        value={note}
        onChange={(event) => {
          setNote(event.target.value);
        }}
      />
      {error === '' ? null : <InlineError>{error}</InlineError>}
    </Dialog>
  );
}
